import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import SetupWizard from '@/components/wizard/SetupWizard';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';

// Inline SVG social icons
function SocialIcon({ href, title, children }: { href: string; title: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title}
      className="text-gray-400 transition-colors hover:text-gray-600">
      {children}
    </a>
  );
}

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const { appName, twitterUrl, linkedinUrl, facebookUrl, copyrightText } = useBrandingStore();
  const wizardKey = `grc-wizard-${user?.id ?? 'anon'}`;

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(wizardKey) === 'true',
  );

  const showWizard = !dismissed;

  const handleDismiss = () => {
    localStorage.setItem(wizardKey, 'true');
    setDismissed(true);
  };

  const year      = new Date().getFullYear();
  const copyright = copyrightText || `\u00a9 ${year} ${appName}. All rights reserved.`;
  const hasSocial = twitterUrl || linkedinUrl || facebookUrl;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-2">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 bg-surface-2">
          <Outlet />
        </main>

        {/* ── App footer ────────────────────────────────────────────────── */}
        <footer className="flex h-10 shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-surface px-3 sm:px-6">
          <p className="text-xs text-gray-400 truncate">{copyright}</p>
          {hasSocial && (
            <div className="flex items-center gap-3">
              {twitterUrl && (
                <SocialIcon href={twitterUrl} title="X / Twitter">
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.734-8.85L1.254 2.25H8.08l4.259 5.629L18.244 2.25zM17.083 19.77h1.833L7.084 4.126H5.117z" /></svg>
                </SocialIcon>
              )}
              {linkedinUrl && (
                <SocialIcon href={linkedinUrl} title="LinkedIn">
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                </SocialIcon>
              )}
              {facebookUrl && (
                <SocialIcon href={facebookUrl} title="Facebook">
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </SocialIcon>
              )}
            </div>
          )}
        </footer>
      </div>
      {showWizard && <SetupWizard onDismiss={handleDismiss} />}
    </div>
  );
}

